import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle, Clock, Users, MapPin, Phone, Mail, AlertCircle } from "lucide-react";

interface ReservaInfo {
  id: number;
  nombreCliente: string;
  telefono?: string;
  email?: string;
  fechaReserva: string;
  horaReserva: string;
  numPersonas: number;
  contexto?: string;
  requerimientos?: string;
  estado: string;
  checkInAt?: string;
  sucursalNombre: string;
  colorPrimario?: string;
  logoUrl?: string;
  cliente?: {
    codCliente: number;
    totalVisitas: number;
    visitasEnSucursal?: number | null;
    ultimaVisita?: string | null;
    suelePedir?: string[];
  } | null;
}

function formatFecha(fecha: string) {
  try {
    return new Date(fecha + "T00:00:00").toLocaleDateString("es-VE", {
      weekday: "long", year: "numeric", month: "long", day: "numeric",
    });
  } catch { return fecha; }
}

export default function CheckInPage() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<ReservaInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    fetch(`/api/v1/reservas/checkin/${token}`)
      .then(r => {
        if (!r.ok) throw new Error("Reserva no encontrada");
        return r.json();
      })
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  const color = data?.colorPrimario ?? "#C0392B";

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center text-gray-400">
          <div className="text-4xl mb-3 animate-pulse">🔍</div>
          <p>Cargando reserva...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white rounded-2xl p-8 max-w-sm w-full text-center shadow-lg">
          <AlertCircle className="size-12 text-red-400 mx-auto mb-3" />
          <h2 className="font-bold text-lg text-gray-800 mb-2">Reserva no encontrada</h2>
          <p className="text-gray-500 text-sm">{error ?? "El código QR no es válido o ha expirado."}</p>
        </div>
      </div>
    );
  }

  const estadoColor: Record<string, string> = {
    pendiente:   "#F59E0B",
    confirmada:  "#10B981",
    atendida:    "#6366F1",
    cancelada:   "#EF4444",
    suspendida:  "#F97316",
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header con color del restaurante */}
      <div className="text-white px-4 py-5 text-center" style={{ backgroundColor: color }}>
        {data.logoUrl && (
          <img src={data.logoUrl} alt="Logo" className="h-12 w-12 rounded-full object-cover mx-auto mb-3 border-2 border-white/50" />
        )}
        <p className="text-sm opacity-80 uppercase tracking-widest font-medium">{data.sucursalNombre}</p>
        <h1 className="text-2xl font-bold mt-1">Check-in</h1>
        {data.checkInAt && (
          <p className="text-sm opacity-75 mt-1">
            ✓ Registrado a las {new Date(data.checkInAt).toLocaleTimeString("es-VE", { hour: "2-digit", minute: "2-digit" })}
          </p>
        )}
      </div>

      <div className="max-w-sm mx-auto px-4 py-6 space-y-4">
        {/* Estado */}
        <div
          className="flex items-center justify-between px-4 py-3 rounded-xl text-white font-semibold"
          style={{ backgroundColor: estadoColor[data.estado] ?? "#6B7280" }}
        >
          <span className="capitalize">{data.estado}</span>
          <span className="text-sm">Reserva #{data.id}</span>
        </div>

        {/* Info principal del cliente */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div
                className="size-12 rounded-full flex items-center justify-center text-white text-xl font-bold"
                style={{ backgroundColor: color }}
              >
                {data.nombreCliente[0].toUpperCase()}
              </div>
              <div>
                <h2 className="font-bold text-gray-900 text-lg">{data.nombreCliente}</h2>
                <p className="text-sm text-gray-500">Cliente</p>
              </div>
            </div>
          </div>

          <div className="px-5 py-4 space-y-3">
            <div className="flex items-center gap-3 text-sm">
              <Clock className="size-4 text-gray-400 shrink-0" />
              <div>
                <p className="font-semibold text-gray-800">{data.horaReserva}</p>
                <p className="text-gray-500 text-xs capitalize">{formatFecha(data.fechaReserva)}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 text-sm">
              <Users className="size-4 text-gray-400 shrink-0" />
              <p className="text-gray-700">{data.numPersonas} persona{data.numPersonas !== 1 ? "s" : ""}</p>
            </div>

            {data.contexto && (
              <div className="flex items-center gap-3 text-sm">
                <MapPin className="size-4 text-gray-400 shrink-0" />
                <p className="text-gray-700">{data.contexto}</p>
              </div>
            )}

            {data.telefono && (
              <div className="flex items-center gap-3 text-sm">
                <Phone className="size-4 text-gray-400 shrink-0" />
                <a href={`tel:${data.telefono}`} className="text-blue-600">{data.telefono}</a>
              </div>
            )}

            {data.email && (
              <div className="flex items-center gap-3 text-sm">
                <Mail className="size-4 text-gray-400 shrink-0" />
                <a href={`mailto:${data.email}`} className="text-blue-600 truncate">{data.email}</a>
              </div>
            )}
          </div>

          {data.requerimientos && (
            <div className="px-5 py-3 bg-amber-50 border-t border-amber-100">
              <p className="text-xs font-semibold text-amber-700 mb-1">💬 Notas especiales</p>
              <p className="text-sm text-amber-800">{data.requerimientos}</p>
            </div>
          )}
        </div>

        {/* Cliente frecuente (si existe en el CRM con historial) */}
        {data.cliente && (
          <div className="bg-white rounded-2xl shadow-sm p-5">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">Cliente frecuente</p>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold" style={{ color }}>{data.cliente.totalVisitas}</span>
              <span className="text-sm text-gray-500">
                visita{data.cliente.totalVisitas !== 1 ? "s" : ""}
                {typeof data.cliente.visitasEnSucursal === "number" && (
                  <> · {data.cliente.visitasEnSucursal} en esta sucursal</>
                )}
              </span>
            </div>
            {data.cliente.ultimaVisita && (
              <p className="text-xs text-gray-500 mt-1 capitalize">
                Última visita: {formatFecha(String(data.cliente.ultimaVisita).slice(0, 10))}
              </p>
            )}
            {data.cliente.suelePedir && data.cliente.suelePedir.length > 0 && (
              <div className="mt-3">
                <p className="text-xs font-semibold text-gray-600 mb-1.5">Suele pedir</p>
                <div className="flex flex-wrap gap-1.5">
                  {data.cliente.suelePedir.map((it, i) => (
                    <span key={i} className="text-xs px-2.5 py-1 rounded-full bg-gray-100 text-gray-700">{it}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Acciones para la anfitriona */}
        {data.estado !== "atendida" && data.estado !== "cancelada" && (
          <div className="grid grid-cols-2 gap-3">
            {data.telefono && (
              <a
                href={`https://wa.me/${data.telefono.replace(/\D/g, '')}?text=Hola%20${encodeURIComponent(data.nombreCliente)}!%20Tu%20mesa%20está%20lista%20en%20${encodeURIComponent(data.sucursalNombre)}`}
                className="flex items-center justify-center gap-2 py-3 rounded-xl bg-green-500 text-white font-medium text-sm"
              >
                💬 WhatsApp
              </a>
            )}
            {data.telefono && (
              <a
                href={`tel:${data.telefono}`}
                className="flex items-center justify-center gap-2 py-3 rounded-xl bg-gray-100 text-gray-700 font-medium text-sm"
              >
                📞 Llamar
              </a>
            )}
          </div>
        )}

        <p className="text-center text-xs text-gray-400 pb-4">
          Esta información es solo para uso interno del personal.
        </p>
      </div>
    </div>
  );
}
